import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {FooterWithControls, ModalMain} from 'shells/modal/step-modal/layout';
import {ModalBody} from 'shells/typography/modal-body';
import {ModalHeader} from 'shells/typography/modal-header';

import {useUpdateCompetitorIqWebsites} from '../graphql/hooks/use-update-competitor-iq-websites';
import {WebsiteField} from '../website-field';

type Props = {
    website: string;
    onSaveWebsite: (website: string) => void;
};

export function ConfirmWebsite(props: Props) {
    const [website, setWebsite] = React.useState<string>(props.website);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const {updateWebsiteSettings} = useUpdateCompetitorIqWebsites();

    React.useEffect(() => {
        if (props.website) {
            setWebsite(props.website);
        }
    }, [props.website]);

    const onSave = () => {
        setIsSaving(true);
        updateWebsiteSettings({website})
            .then(() => {
                setIsSaving(false);

                props.onSaveWebsite(website);
            })
            .catch((e) => {
                setErrorMessage(safelyGetGraphQLErrorMessage(e));
                setIsSaving(false);
            });
    };

    return (
        <>
            <ModalMain>
                <ModalHeader>Confirm your website</ModalHeader>
                <StackLegacy spacing={8}>
                    <ModalBody>
                        Add or confirm your company’s website to track your online presence and
                        compare it with competitors. If we’ve already detected it, we’ve prefilled
                        the field for your convenience. Just give it a quick confirmation to get
                        started.
                    </ModalBody>
                    <WebsiteField
                        website={website}
                        setWebsite={setWebsite}
                        isWebsiteSaving={isSaving}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        isSaveDisabled={!website || website === props.website}
                    />
                </StackLegacy>
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={0} slideCount={3} />}
                buttons={
                    <Button
                        variant='primary'
                        size='big'
                        onClick={onSave}
                        disabled={Boolean(!website || errorMessage)}
                    >
                        {isSaving ? 'Confirming…' : 'Confirm website'}
                    </Button>
                }
            />
        </>
    );
}
